/*
 * Copyright 2026 North Carolina State University
 */

// Helpers for working with "life expectancy" values that are stored as a number
// of HOURS. We derive days and months for display (24 hours/day, ~30.44 days/month
// which is the average month length across a year).

const HOURS_PER_DAY = 24;
const DAYS_PER_MONTH = 30.44;

// Round to at most `decimals` places without trailing zeros (e.g. 4.0 -> "4", 4.17 -> "4.17").
const trim = (num, decimals = 1) => {
  if (!Number.isFinite(num)) return '';
  return parseFloat(num.toFixed(decimals)).toString();
};

// Convert a number of hours into { hours, days, months } numeric values.
export const hoursToParts = (hours) => {
  const h = Number(hours);
  if (!Number.isFinite(h) || h < 0) return null;
  const days = h / HOURS_PER_DAY;
  const months = days / DAYS_PER_MONTH;
  return { hours: h, days, months };
};

// Short label for the hours value itself, e.g. "10000 hrs" or "100.5 hrs".
export const formatHoursLabel = (hours) => {
  const parts = hoursToParts(hours);
  if (!parts) return '—';
  return `${trim(parts.hours, 2)} hrs`;
};

// Human readable conversion, e.g. "≈ 416.7 days (~13.7 months)".
export const formatHoursConversion = (hours) => {
  const parts = hoursToParts(hours);
  if (!parts) return '';
  return `\u2248 ${trim(parts.days, 1)} days (~${trim(parts.months, 1)} months)`;
};

// Combined label + conversion for tooltips, e.g.
// "10000 hrs ≈ 416.7 days (~13.7 months)".
export const formatHoursDetailed = (hours) => {
  const parts = hoursToParts(hours);
  if (!parts) return '';
  return `${formatHoursLabel(hours)} ${formatHoursConversion(hours)}`;
};
